import * as React from 'react';
import { connect } from 'react-redux';

import { Form, FormField } from 'components/form';
import Input from 'components/inputs/input/Input';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import ActionButton from 'components/buttons/action-button/ActionButton';
import { ModalFrame, ModalFrameFooter, ModalFrameInner } from 'components/modal';

import PromptExit from 'libs/router/prompt-exit/PromptExit';

import { duplicateCollection } from 'modules/lists/reducers/lists';

import { __t, applyTranslations } from 'utils/language';
import { isNotEmpty, maxLength } from 'utils/validators';

import './collection-duplicate-modal.scss';

import type {
  Account,
  Data,
  MixedSelectDropdownItem
} from 'types';

applyTranslations(require('./collection-duplicate-modal.i18n.json'));
applyTranslations(require('locales/common/collabary-common.i18n.json'));

type OwnProps = {
  collectionId: number,
  currentOrgId: number,
  onCloseModal: () => void,
  onDuplicate?: () => void
};

type StateProps = {
  currentAccount: Account
};

type DispatchProps = {
  dispatchDuplicateCollection(
    currentOrgId: number,
    collectionId: number,
    destOrgId: number,
    newName: string,
    newDescription: string
  ): Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

const LABEL_MAX_LENGTH = 60;

const CollectionDuplicateModal = (
  {
    collectionId,
    currentOrgId,
    currentAccount,
    onCloseModal,
    onDuplicate,
    dispatchDuplicateCollection
  }: Props
): JSX.Element => {
  const [isModified, setIsModified] = React.useState(false);
  const [brand, setBrand] = React.useState(String(currentOrgId));
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');

  const brands = currentAccount.organizations
    .filter((org) => org.type === 'brand');

  const brandSelectItems: Array<MixedSelectDropdownItem> = brands
    .map(entry => ({
      identifier: String(entry.id),
      isDisabled: false,
      isSelected: brand === String(entry.id),
      label: entry.name,
      value: String(entry.id)
    }));

  const handleBrandChange = (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null,
    selectedItem?: Partial<MixedSelectDropdownItem> | null
  ): void => (
    setBrand(selectedItem?.identifier || updatedItems[0].identifier)
  );

  return (
    <ModalFrame
      title={__t('collection_duplicate_modal.modal_title')}
    >
      {
        isModified &&
        <PromptExit type="modal" />
      }
      <ModalFrameInner
        narrowContent
      >
        <Form
          className="collection-duplicate-modal__form"
          onSubmit={async () => {
            const destBrandId = Number(brand);
            await dispatchDuplicateCollection(currentOrgId, collectionId, destBrandId, title, description);
            if (onDuplicate && currentOrgId === destBrandId) {
              onDuplicate();
            }
            onCloseModal();
          }}
        >
          {brands.length > 1 && (
            <FormField
              label={__t('collection_duplicate_modal.brand_select_label')}
              labelType="highlighted"
              direction="column"
              fontSize="small"
              showRequiredMark
              showValidationIcon={false}
              className="collection-duplicate-modal__brands"
              validators={[
                isNotEmpty(title)
              ]}
            >
              <MixedSelectDropdown
                key={`collection-duplicate-modal__dropdown--${brand}`}
                name="collection-duplicate-modal__dropdown"
                items={brandSelectItems}
                withSelectedInList
                className="collection-duplicate-modal__dropdown"
                onItemsToggle={handleBrandChange}
              />
            </FormField>
          )}

          <FormField
            label={__t('collection_duplicate_modal.collection_name_label')}
            labelType="highlighted"
            direction="column"
            fontSize="small"
            showRequiredMark
            showValidationIcon={false}
            validators={[
              isNotEmpty(title),
              maxLength(title, LABEL_MAX_LENGTH)
            ]}
          >
            <Input
              name="list-label"
              value={title}
              width="responsive"
              data={{ key: 'title' }}
              onChange={(event) => {
                setTitle(event.target.value);
                setIsModified(true);
              }}
              maxLength={LABEL_MAX_LENGTH}
              focusOnMount
            />
          </FormField>

          <FormField
            label={__t('collection_duplicate_modal.description_label')}
            labelType="highlighted"
            direction="column"
            fontSize="small"
            showValidationIcon={false}
          >
            <Input
              name="list-description"
              value={description}
              width="responsive"
              multiline
              placeholder={__t('collection_duplicate_modal.description_placeholder')}
              data={{ key: 'description' }}
              onChange={(event) => {
                setDescription(event.target.value);
                setIsModified(true);
              }}
            />
          </FormField>

          <ModalFrameFooter>
            <ActionButton
              color="tertiary"
              onClick={onCloseModal}
            >
              {__t('collabary_common.action_button.cancel')}
            </ActionButton>
            <ActionButton
              color="primary"
              type="submit"
            >
              {__t('collection_duplicate_modal.submit_button_label')}
            </ActionButton>
          </ModalFrameFooter>
        </Form>
      </ModalFrameInner>
    </ModalFrame>
  );
};

const mapStateToProps = ({ account }): StateProps => ({
  currentAccount: account.currentAccount
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchDuplicateCollection: (
    currentOrgId: number,
    collectionId: number,
    destOrgId: number,
    newName: string,
    newDescription: string
  ) => dispatch(duplicateCollection(currentOrgId, collectionId, destOrgId, newName, newDescription))
});

export default connect(mapStateToProps, mapDispatchToProps)(CollectionDuplicateModal);
